// REACT IMPORTS: Core React functionality
import React from "react";
import { QueryClient, QueryClientProvider } from "react-query";

// ROUTING IMPORTS: React Router for client-side navigation
import {BrowserRouter as Router, Routes, Route, useNavigate} from "react-router-dom";

// COMPONENT IMPORTS: Page components and UI elements
import {PrivacyPolicy} from "./components/PrivacyPolicy";  // Privacy policy page
import {TermsAndConditions} from "./components/TermsAndConditions";  // Terms of use page
import {Footer} from "./components/Footer";                // Footer component
import {Nav, WelcomeNav} from "./components/Nav";          // Navigation component
import LandingPage from "./components/LandingPage";        // Landing page for unauthenticated users
import MainApp from "./components/MainApp";                // Main recipe app for authenticated users
import SavedRecipes from "./components/SavedRecipes";      // Saved recipes page for premium users
import About from "./components/About";                    // Public about page
import Support from "./components/Support";                // Public support page
import UpdatePasswordPage from "./pages/UpdatePasswordPage";

// AUTHENTICATION: Context and hooks
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { RecipeSearchProvider } from "./contexts/RecipeSearchContext";

/**
 * Protected App Content Component
 * Shows different content based on authentication status
 * - Unauthenticated: Landing page with auth gateway
 * - Authenticated: Main recipe application
 */
const AppContent = () => {
  const { user, loading } = useAuth()

  // Show loading spinner while checking auth status
  if (loading) {
    return (
      <div className="min-h-screen bg-recipe-gradient flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <div className="text-white text-xl font-semibold mb-2">Welcome to Yummtime!</div>
          <div className="text-white/80 text-lg">Setting up your account...</div>
        </div>
      </div>
    )
  }

  // Show landing page for unauthenticated users
  if (!user) {
    return <LandingPage />
  }

  // Show main app for authenticated users
  return (
    <>
      <Nav />
      <MainApp />
      <Footer />
    </>
  )
}

const PublicPageNav = () => {
  const { user } = useAuth()
  const navigate = useNavigate()

  if (user) {
    return <Nav />
  }

  return <WelcomeNav onGetStarted={() => navigate('/')} />
}

const PrivacyPage = () => {
  return (
    <>
      <PublicPageNav />
      <div className="recipe-app-shell min-h-screen bg-recipe-gradient py-8">
        <PrivacyPolicy />
      </div>
      <Footer />
    </>
  )
}

const TermsPage = () => {
  return (
    <>
      <PublicPageNav />
      <div className="recipe-app-shell min-h-screen bg-recipe-gradient py-8">
        <TermsAndConditions />
      </div>
      <Footer />
    </>
  )
}
const AboutPage = () => {
  return (
    <>
      <PublicPageNav />
      <div className="recipe-app-shell min-h-screen bg-recipe-gradient py-8">
        <About />
      </div>
      <Footer />
    </>
  )
}
const SupportPage = () => {
  return (
    <>
      <PublicPageNav />
      <div className="recipe-app-shell min-h-screen bg-recipe-gradient py-8">
        <Support />
      </div>
      <Footer />
    </>
  )
}

const ProtectedSavedRecipesPage = () => {
  const { user, userProfile, loading, profileLoading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen bg-recipe-gradient flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <div className="text-white text-xl font-semibold mb-2">Welcome to Yummtime!</div>
          <div className="text-white/80 text-lg">Loading saved recipes...</div>
        </div>
      </div>
    )
  }

  if (!user) {
    return <LandingPage />
  }

  const isPremium = userProfile?.tier === 'premium'

  return (
    <>
      <Nav />
      <div className="recipe-app-shell min-h-screen bg-recipe-gradient py-8">
        {profileLoading ? (
          <div className="text-center text-white text-lg">Checking your account...</div>
        ) : isPremium ? (
          <SavedRecipes />
        ) : (
          <div className="mx-auto max-w-2xl px-4">
            <div className="rounded-lg bg-white p-8 text-center shadow-lg">
              <h1 className="text-2xl font-semibold text-brand mb-3">Saved Recipes</h1>
              <p className="text-gray-700">
                Saved recipes are available for Premium accounts.
              </p>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </>
  )
}

const App = () => {
  const [queryClient] = React.useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        cacheTime: 1000 * 60 * 60,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: Infinity
      }
    }
  }))

  // RENDER METHOD: New routing structure with authentication-based content
  return (
    <QueryClientProvider client={queryClient}>
      <RecipeSearchProvider>
        <AuthProvider>
          <Router>
            <Routes>
              {/* Main app route - shows different content based on auth status */}
              <Route path="/" element={<AppContent />} />

              {/* Public about route */}
              <Route path="/about" element={<AboutPage />} />
              
              {/* Public privacy route */}
              <Route path="/privacy" element={<PrivacyPage />} />

              {/* Public terms route */}
              <Route path="/terms" element={<TermsPage />} />

              {/* Public support route */}
              <Route path="/support" element={<SupportPage />} />

              {/* Password recovery route */}
              <Route path="/update-password" element={<UpdatePasswordPage />} />

              {/* Saved recipes route - only accessible to premium users */}
              <Route path="/saved-recipes" element={<ProtectedSavedRecipesPage />} />
            </Routes>
          </Router>
        </AuthProvider>
      </RecipeSearchProvider>
    </QueryClientProvider>
  )
}


export default App;
